import { createI18n } from 'vue-i18n';
import moment from 'moment/moment';
import vuetify from '@/plugins/vuetify';
import en from './lang/en.json';

export const LANGUAGES = {
  en:{
    id: 'en',
    name: 'English'
  },
  es:{
    id: 'es',
    name: 'Español'
  },
  zh:{
    id: 'zh',
    name: '中文'
  },
  ru:{
    id: 'ru',
    name: 'Русский'
  }
};


export const loadedLanguages = ['en'] // our default language that is preloaded

export const availableLanguages = ['en','es','zh','ru']

const messages = {
  en
}

const initialLanguage = ()=>{
  const navigatorLocale =
    navigator.languages !== undefined
      ? navigator.languages[0]
      : navigator.language
  if (!navigatorLocale) {
    return undefined
  }
  const browserLanguage  = navigatorLocale.trim().split(/-|_/)[0]

  const lang  = availableLanguages.includes(browserLanguage) ? browserLanguage  : 'en'

  if(lang==='en') return lang;

  import(/* webpackChunkName: "lang-[request]" */ `@/i18n/lang/${lang}.json`).then(
    messages => {
      i18n.global.setLocaleMessage(lang, messages.default)
      loadedLanguages.push(lang)
    }
  )
  return lang
}


const i18n = createI18n({
  legacy: false,
  locale: initialLanguage(),
  fallbackLocale: 'en',
  allowComposition: true,
  globalInjection: true,
  fallbackWarn: false, // temporarily hide intlify fallback notifications
  missingWarn: false,
  warnHtmlMessage: false,
  formatter: {
    interpolate(message, values, path) {
      if (message.lastIndexOf('{') !== -1) {
        message = message
          .replace(/{([^\\]*?)}/gmi, d => {
            const value = values[d[1]]
            if (value || value===0) return value
            return d
          })
          .replace(/\\{(.+?)\\}/gmi, '{$1}');
      }
      return [message];
    }
  },
  missing: function (lang,key,c) {
    if (import.meta.env.VUE_APP_CONFIG_NAME !== 'production') {
      console.log(`missing i18n key: ${key}`);
    }

  },
  pluralizationRules: {
    /**
     * @param choice {number} a choice index given by the input to $tc: `$tc('path.to.rule', choiceIndex)`
     * @param choicesLength {number} an overall amount of available choices
     * @returns a final choice index to select plural word by
     */
    'ru': function(choice, choicesLength) {
      //0 things | things count ends with 1 | things count ends with 2-4 | things count ends with 5-9, 0 and teens (10-19)

      // this === VueI18n instance, so the locale property also exists here

      if (choice === 0) {
        return 0;
      }

      const teen = choice > 10 && choice < 20;
      const endsWithOne = choice % 10 === 1;

      if (choicesLength < 4) {
        return (!teen && endsWithOne) ? 1 : 2;
      }
      if (!teen && endsWithOne) {
        return 1;
      }
      if (!teen && choice % 10 >= 2 && choice % 10 <= 4) {
        return 2;
      }

      return (choicesLength < 4) ? 2 : 3;
    }
  },
  messages
});

const setI18nLanguage = (lang: string) => {
  i18n.global.locale.value = lang;
  moment.locale(lang === 'zh' ? 'zh-cn' : lang);
  vuetify.locale.current = lang === 'zh' ? 'zhHans' : lang;
  document.querySelector('html')?.setAttribute('lang', lang);
  return lang;
};

export const loadLanguageAsync = async (lang: string) => {
  let langToApply = lang;
  if (!availableLanguages.includes(langToApply)) {
    langToApply = 'en';
  }

  if (i18n.global.locale.value === langToApply) {
    return Promise.resolve(setI18nLanguage(langToApply));
  }

  if (loadedLanguages.includes(langToApply)) {
    return Promise.resolve(setI18nLanguage(langToApply));
  }

  const modules = import.meta.glob<{ default: Record<string, unknown> }>('@/i18n/lang/*.json');
  const path = `/src/i18n/lang/${langToApply}.json`;
  const loader = modules[path] ?? modules[Object.keys(modules).find((k) => k.endsWith(`${langToApply}.json`))!];

  if (loader) {
    const messages = await loader();
    i18n.global.setLocaleMessage(langToApply, messages.default);
    loadedLanguages.push(langToApply);
    return setI18nLanguage(langToApply);
  }
};

export default i18n;
